import { useLocation, useParams } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import {
    SLIDE_QUERY_PARAM,
    DFM_SOURCE_TYPE_3D,
} from '@Admin/AdminQuotingPage/constants'
import {
    DFM_FICTIV_REVIEW_REQUESTED,
    DFM_PENDING_REVISION,
    ImDfmStateType,
} from '@Admin/AdminQuotingPage/ImDfmSlidesPanel/ImDfmSlide/constants'
import quoteStatusQuery from '@Builder/QuoteDetailPage/FilesTab/ImQuoteTabSections/ManufacturabilityFeedbackTab/DfmCategorySection/quoteStatusQuery'
import { PROVISIONED_UI_FLAGS } from '@Builder/QuoteDetailPage/logic'

import Box from '~/core/Box'
import { useExtractUrlSearchParams } from '~/lib/extractUrlSearchParams'
import { REQUESTED } from '~/lib/quoteStatuses'
import imDfmSlidesPanelQuery from '../ImDfmSlidesPanel/imDfmSlidesPanelQuery'
import { imDfmSlidesPanel_quoteModelRevision_dfmFeedback as DfmFeedbackType } from '../ImDfmSlidesPanel/__generated__/imDfmSlidesPanel'
import { useFlagEnabled } from '~/lib/isFlagEnabled'

import AnnotateSlideButton from './AnnotateSlideButton'
import ChangeResponseMsg from './ChangeResponseMsg'
import PrimaryActionButton from './PrimaryActionButton'
import ProTip from './ProTip'
import SlideStatusAlert from './SlideStatusAlert'
import UploadRevisionButton from './UploadRevisionButton'
import ImDfm3dSlideInfo from './ImDfm3dSlideInfo'
import { DFM_STATUS_ACTIONS } from './constants'

interface SlideActionPanelPropsType {
    isImQuoteType: boolean
}

const PLACEMENT = 'im-dfm-slide-action-panel'

const SlideActionPanel = (props: SlideActionPanelPropsType) => {
    const { isImQuoteType } = props

    const { search } = useLocation()
    const { qmrId, quoteId, id } = useParams<{
        qmrId: string
        id: string
        quoteId?: string
    }>()

    const { data } = useQuery(imDfmSlidesPanelQuery, {
        variables: { id: qmrId },
        fetchPolicy: 'cache-first',
    })

    const { data: quoteStatusData } = useQuery(quoteStatusQuery, {
        variables: { id: id || quoteId },
        skip: !(quoteId || id),
    })
    const isLocked = quoteStatusData?.quote?.isLocked

    const quoteStatus = quoteStatusData?.quote?.status

    const searchParams = useExtractUrlSearchParams(search, [SLIDE_QUERY_PARAM])

    const qmr = data?.quoteModelRevision
    const isNonImDfmEnabled = useFlagEnabled(PROVISIONED_UI_FLAGS.NON_IM_DFM_UI)
    const isHighTouch = data?.quoteModelRevision?.quoteModel?.quote?.isCpqOwned
    const activeSlideId = searchParams[SLIDE_QUERY_PARAM]
    const dfmFeedback = data?.quoteModelRevision?.dfmFeedback || []
    const activeSlide = dfmFeedback.find(
        (dfm: DfmFeedbackType) => dfm.id === activeSlideId,
    )

    const activeSlideStatus = activeSlide?.status as ImDfmStateType
    const acceptedAt = activeSlide?.acceptedAt
    const acceptedByEmail = activeSlide?.acceptedBy?.email
    const topic = activeSlide?.topic?.label
    const message = activeSlide?.message
    const title = activeSlide?.title

    const isFictivReviewRequested = quoteStatus === REQUESTED
    const canAnnotate = DFM_STATUS_ACTIONS[activeSlideStatus]?.canAnnotate
    const hasPartRevision = Boolean(
        qmr?.version !== qmr?.quoteModel?.selectedQuoteModelRevision?.version,
    )
    const is3dDfmSlide = activeSlide?.sourceType === DFM_SOURCE_TYPE_3D

    if (isFictivReviewRequested) {
        return (
            <Box
                direction="col"
                justifyContent="between"
                margin="8px"
                width="100%"
            >
                <SlideStatusAlert
                    acceptedAt={acceptedAt}
                    acceptedByEmail={acceptedByEmail}
                    status={DFM_FICTIV_REVIEW_REQUESTED}
                />
            </Box>
        )
    }

    return (
        <Box direction="col" justifyContent="between" margin="8px" width="100%">
            <Box direction="col" justifyContent="center">
                <SlideStatusAlert
                    acceptedAt={acceptedAt}
                    acceptedByEmail={acceptedByEmail}
                    status={activeSlideStatus}
                />
                {is3dDfmSlide && (
                    <ImDfm3dSlideInfo
                        topic={topic}
                        message={message}
                        title={title}
                        isHighTouch={!isImQuoteType && isHighTouch}
                        isNonImDfmEnabled={isNonImDfmEnabled}
                    />
                )}
                <PrimaryActionButton
                    status={activeSlideStatus}
                    disabled={hasPartRevision}
                />
                {canAnnotate && !hasPartRevision && (
                    <AnnotateSlideButton status={activeSlideStatus} />
                )}
                {activeSlideStatus === DFM_PENDING_REVISION && qmrId && (
                    <UploadRevisionButton
                        isImQuoteType={isImQuoteType}
                        isLocatedInImDfmModal={true}
                        isPreviousQmrRevision={hasPartRevision}
                        placement={PLACEMENT}
                        qmrId={qmrId}
                        isQuoteLocked={isLocked}
                    />
                )}
                {!hasPartRevision && (
                    <ChangeResponseMsg status={activeSlideStatus} />
                )}
            </Box>
            <ProTip />
        </Box>
    )
}

export default SlideActionPanel
